package com.example.mystybag.login.views

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import androidx.compose.foundation.Image
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.offset
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.common.resources.SR
import com.example.mystybag.config.ApplicationConfig
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p52
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.common.ui.components.native.CaptionCapitalization
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonSize
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.rememberIsWebPortraitLayout
import com.example.mystybag.login.logic.WelcomeViewModel
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.core.views.AppTheme

@Composable
fun WelcomeView(viewModel: WelcomeViewModel) {
    if (PlatformHelper.isWeb) {
        if (rememberIsWebPortraitLayout()) {
            WelcomeViewMobile(viewModel)
        } else {
            WelcomeViewWeb(viewModel)
        }
        return
    }
    WelcomeViewMobile(viewModel)
}

@Composable
private fun WelcomeViewMobile(viewModel: WelcomeViewModel) {
    val state = viewModel.uiState.subscribeAsState()
    UIView(
        modifier            = Modifier,
        viewModel           = viewModel,
        horizontalAlignment = Alignment.CenterHorizontally,
        removeVerticalPadding = true,
        showTopBar          = false,
        applyBottomInsetSpacer = false
    ) { uiState ->
        val merchantOnly = ApplicationConfig.isMerchantOnlyBuild
        // In a merchant-only build the segment control is hidden, so we always
        // render the merchant branch regardless of the stored UI state.
        val isClientTab = !merchantOnly && uiState.value.isClientTabSelected
        val colors = AppTheme.current.colors

        // Image only in top portion; white card overlaps so image "enters under" the white part
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(colors.surface)
        ) {
            Image(
                painter = painterResource(SR.images.img_welcome_bag),
                contentDescription = null,
                modifier = Modifier
                    .align(Alignment.TopCenter)
                    .fillMaxWidth()
                    .fillMaxHeight(0.6f),
                contentScale = ContentScale.Crop,
                alignment = Alignment.TopCenter
            )

            val bottomSafeArea = WindowInsets.navigationBars.asPaddingValues().calculateBottomPadding()

            // On web the visible viewport is shorter (browser chrome), so the card needs
            // more height to fit all four social buttons without clipping. Native keeps 0.52f.
            val cardHeightFraction = if (PlatformHelper.isWeb) 0.62f else 0.52f

            // White card overlaps bottom of image (rounded top), so only part of image is visible and it goes under the card
            Column(
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .fillMaxWidth()
                    .fillMaxHeight(cardHeightFraction)
                    .background(
                        color = colors.surface,
                        shape = RoundedCornerShape(topStart = p48, topEnd = p48)
                    )
                    .clip(RoundedCornerShape(topStart = p48, topEnd = p48))
                    .verticalScroll(rememberScrollState())
                    .padding(start = p16, end = p16, top = p32, bottom = p8 + bottomSafeArea),
                verticalArrangement = Arrangement.spacedBy(p10)
            ) {
                // Title and Client/Merchant segment control are hidden in
                // merchant-only builds (see ApplicationConfig.isMerchantOnlyBuild).
                if (!merchantOnly) {
                    // Title: "What kind of user are you?" (Headline Small Medium 24) – centered per Figma
                    Box(
                        modifier = Modifier.fillMaxWidth(),
                        contentAlignment = Alignment.Center
                    ) {
                        Texts.HeadlineSmall(
                            text = stringResource(SR.strings.what_kind_of_user_are_you),
                            color = colors.onSurface
                        )
                    }

                    // Segment control — same as "Моите резервации": sliding pill animation
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(48.dp)
                            .clip(RoundedCornerShape(200.dp))
                            .background(colors.surfaceContainerLowest)
                            .padding(4.dp)
                    ) {
                        BoxWithConstraints(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(40.dp)
                        ) {
                            val segmentWidth = maxWidth / 2
                            val tabIndex = if (isClientTab) 0f else 1f
                            val fraction by animateFloatAsState(
                                targetValue = tabIndex,
                                animationSpec = tween(durationMillis = 250),
                                label = "segmentIndicator"
                            )
                            val slidingOffsetDp = (segmentWidth.value * fraction).dp
                            // Sliding white pill (behind text)
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .offset(x = slidingOffsetDp)
                            ) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth(0.5f)
                                        .fillMaxHeight()
                                        .clip(RoundedCornerShape(200.dp))
                                        .background(colors.surface)
                                )
                            }
                            // Labels on top (no background, clickable)
                            Row(
                                modifier = Modifier.fillMaxSize(),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                listOf(
                                    stringResource(SR.strings.client) to true,
                                    stringResource(SR.strings.merchant) to false
                                ).forEachIndexed { _, (label, isClient) ->
                                    Box(
                                        modifier = Modifier
                                            .weight(1f)
                                            .fillMaxWidth()
                                            .height(40.dp)
                                            .clickable(
                                                interactionSource = remember { MutableInteractionSource() },
                                                indication = null
                                            ) { viewModel.onTabSelected(isClient) },
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Texts.BodyLarge(
                                            text = label,
                                            color = if (isClientTab == isClient) colors.primary else colors.onSurfaceVariant
                                        )
                                    }
                                }
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(p24))
                }

                if (isClientTab) {
                    // Client: 4 buttons – white background + outline only (Figma), 8dp spacing, height 52, cornerRadius 100
                    Column(verticalArrangement = Arrangement.spacedBy(p8)) {
                        DefaultButton(
                            modifier = Modifier.fillMaxWidth(),
                            type = DefaultButtonType.OUTLINED_SURFACE,
                            caption = stringResource(SR.strings.continue_with_facebook),
                            capitalization = CaptionCapitalization.None,
                            icon = painterResource(SR.images.ic_facebook),
                            height = p52,
                            callback = viewModel::onFacebookLogin,
                            identifier = "Welcome Facebook"
                        )
                        DefaultButton(
                            modifier = Modifier.fillMaxWidth(),
                            type = DefaultButtonType.OUTLINED_SURFACE,
                            caption = stringResource(SR.strings.continue_with_email),
                            capitalization = CaptionCapitalization.None,
                            icon = painterResource(SR.images.ic_mail),
                            height = p52,
                            callback = viewModel::onEmailLogin,
                            identifier = "Welcome Email"
                        )
                        DefaultButton(
                            modifier = Modifier.fillMaxWidth(),
                            type = DefaultButtonType.OUTLINED_SURFACE,
                            caption = stringResource(SR.strings.continue_with_google),
                            capitalization = CaptionCapitalization.None,
                            icon = painterResource(SR.images.ic_google),
                            height = p52,
                            callback = viewModel::onGoogleLogin,
                            identifier = "Welcome Google"
                        )
                        DefaultButton(
                            modifier = Modifier.fillMaxWidth(),
                            type = DefaultButtonType.OUTLINED_SURFACE,
                            caption = stringResource(SR.strings.continue_with_apple),
                            capitalization = CaptionCapitalization.None,
                            icon = painterResource(SR.images.ic_apple),
                            height = p52,
                            callback = viewModel::onAppleLogin,
                            identifier = "Welcome Apple"
                        )
                    }
                } else {
                    // Merchant: 3 items, 20dp spacing (Figma Frame 26080488), heights 56
                    Column(verticalArrangement = Arrangement.spacedBy(p20)) {
                        // Primary: "Влез като търговец"
                        DefaultButton(
                            modifier = Modifier.fillMaxWidth(),
                            type = DefaultButtonType.PRIMARY,
                            size = DefaultButtonSize.LARGE,
                            caption = stringResource(SR.strings.login_as_merchant),
                            capitalization = CaptionCapitalization.None,
                            callback = viewModel::onMerchantLogin,
                            identifier = "Welcome Merchant Login"
                        )
                        // Secondary: "Влез като служител"
                        DefaultButton(
                            modifier = Modifier.fillMaxWidth(),
                            type = DefaultButtonType.SECONDARY,
                            size = DefaultButtonSize.LARGE,
                            caption = stringResource(SR.strings.login_as_staff),
                            capitalization = CaptionCapitalization.None,
                            callback = viewModel::onStaffLogin,
                            identifier = "Welcome Staff Login"
                        )
                        // Tertiary (text link): "Create account" – centered
                        Box(
                            modifier = Modifier.fillMaxWidth(),
                            contentAlignment = Alignment.Center
                        ) {
                            DefaultButton(
                                modifier = Modifier.wrapContentWidth(),
                                type = DefaultButtonType.TERTIARY,
                                caption = stringResource(SR.strings.continue_as_merchant),
                                capitalization = CaptionCapitalization.None,
                                callback = viewModel::onMerchantRegistration,
                                identifier = "Welcome Merchant Registration"
                            )
                        }
                    }
                }
            }
        }
    }
}
